import { useT } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useEffect, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { PhoneField, Screen, SensitiveField, TextField } from '@/modules/ui';

import { useAccountForm } from '../hooks/useAccountForm';
import { useAccountLegalDocuments } from '../hooks/useAccountLegalDocuments';
import { useSignIn } from '../hooks/useSignIn';
import { purlAccountSchema } from '../schema/accountSchema';
import { buildPurlSignUpPayload } from '../utils/buildPurlSignUpPayload';

import { AccountHeader } from './AccountHeader';
import { AgreementsSection } from './AgreementsSection';
import { Footer } from './Footer';
import { SignInBottomSheet } from './SignInBottomSheet';

// PURL ("Personalized URL") account creation — the second PURL screen, reached
// once the DOB has been confirmed on the entry screen (purl.verifiedDob is set).
// Collects email + optional phone + password + consents and POSTs to sign-up
// with that verified DOB. A lockout / already-enrolled sends the member to login.
// Name and DOB aren't collected here: identity comes from the b2b eligibility
// record the token resolves to, and the DOB was verified on the entry screen.
export function PurlAccountCreationScreen() {
  const t = useT();

  // The org name was resolved at validate time and lives on `purl` (the `?org=`
  // URL param is already gone by this screen) — read it from state, don't re-read
  // the URL.
  const { purl, submitPurlAccount } = useRulesEngine();
  const { legalGroups, legalAdditionalLanguages, legalError, isLegalUnavailable } =
    useAccountLegalDocuments();
  const { control, handleSubmit, setError, persistDraft } = useAccountForm(
    purlAccountSchema,
    legalGroups
  );
  const { signInOpen, setSignInOpen, onSignIn } = useSignIn();

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Fire the PURL "account creation shown" analytics event at most once.
  const hasFiredShownRef = useRef(false);

  const orgName = purl.orgName;
  const title = orgName
    ? t('Good news! Vida is offered to you by {org}', { org: orgName })
    : t('Good news! You may be eligible for Vida through your employer or health plan');

  const onContinue = handleSubmit(async (values) => {
    // The DOB was verified on the entry screen; both are required to sign-up.
    if (!purl.token || !purl.verifiedDob) return;
    persistDraft(values);

    try {
      setIsSubmitting(true);
      trackEvent('purl-account-creation-submit-attempted', {});
      // On success sign-up returns JWT tokens (stored inside the api fn) so
      // refreshUser() — called by submitPurlAccount — flips authStatus and the
      // resolver advances past the PURL stage.
      const result = await submitPurlAccount(
        buildPurlSignUpPayload(values, legalGroups, purl.token, purl.verifiedDob)
      );
      if (result.status === 'created') {
        trackEvent('purl-account-created', {});
      } else if (result.status === 'locked') {
        // The DOB was already verified on the entry screen, so a mismatch here is
        // not expected — a lockout still routes the member to login.
        trackEvent('purl-dob-locked', {});
        setError('email', { message: t('Too many attempts. Please sign in or contact support.') });
        onSignIn();
      } else if (result.status === 'already_enrolled') {
        trackEvent('purl-account-creation-collision', {});
        onSignIn();
      } else {
        // mismatch (unexpected) / configuration_error / network. Surface it on the
        // email field so the message sits below it with the invalid (red) border.
        setError('email', { message: t('Could not create your account. Please try again.') });
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    if (hasFiredShownRef.current) return;
    hasFiredShownRef.current = true;
    trackEvent('purl-account-creation-shown', {});
  }, []);

  return (
    <>
      <Screen
        footer={
          <Footer
            showSignIn
            isSubmitting={isSubmitting || isLegalUnavailable}
            additionalLanguages={legalAdditionalLanguages}
            onContinue={onContinue}
            onSignIn={onSignIn}
            space='2xl'
          />
        }
      >
        <VStack space='2xl'>
          <AccountHeader
            title={title}
            subtitle={t('Create a password to finish setting up your account.')}
          />

          <TextField
            control={control}
            name='email'
            placeholder={t('Email address')}
            autoCapitalize='none'
            autoComplete='email'
            keyboardType='email-address'
            inputMode='email'
            textContentType='emailAddress'
            testID='account-creation-email'
          />
          <PhoneField
            control={control}
            name='phone'
            placeholder={t('Phone number (optional)')}
            testID='account-creation-phone'
          />
          <SensitiveField
            control={control}
            name='password'
            placeholder={t('Password')}
            autoComplete='new-password'
            textContentType='newPassword'
            testID='account-creation-password'
          />

          <AgreementsSection
            control={control}
            legalGroups={legalGroups}
            legalError={legalError}
            showSms
          />
        </VStack>
      </Screen>

      <SignInBottomSheet isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
}
